import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateTime, Info } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { GatheringFormDialog } from '@/components/programs/gathering-form-dialog';
import { detailPathFor, type GatheringKind } from '@/components/programs/gathering-schedule';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatDate, formatTime } from '@/utils/format';

import {
  indexProgramsByDate,
  occurrenceStubForDate,
  type CalendarStreamItem,
} from '@/lib/calendar-stream';

const firstDayOfWeek = (locale: string): number => {
  try {
    const loc = new Intl.Locale(locale);
    const value = (loc as Intl.Locale & { weekInfo?: { firstDay: number } }).weekInfo?.firstDay;
    if (value === 7) return 7;
    if (value && value >= 1 && value <= 7) return value;
  } catch {
    // fall through
  }
  return 1;
};

const buildGrid = (month: DateTime, weekStart: number): Array<string | null> => {
  const start = month.startOf('month');
  const end = month.endOf('month');
  const pad = (start.weekday - weekStart + 7) % 7;
  const cells: Array<string | null> = Array.from({ length: pad }, () => null);
  let cursor = start;
  while (cursor <= end) {
    cells.push(cursor.toISODate());
    cursor = cursor.plus({ days: 1 });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

type CalendarItem = CalendarStreamItem;
type CalendarOccurrenceStub = NonNullable<CalendarStreamItem['occurrences']>[number];

const programDateLabel = (
  item: CalendarItem,
  locale: string,
  selectedDate: string | null,
): string => {
  if (item.programKind === 'service' && selectedDate) {
    return formatDate(selectedDate, { locale, style: 'medium' });
  }
  if (
    item.programStartsOn &&
    item.programEndsOn &&
    item.programStartsOn !== item.programEndsOn
  ) {
    return `${formatDate(item.programStartsOn, { locale, style: 'medium' })} – ${formatDate(item.programEndsOn, { locale, style: 'medium' })}`;
  }
  if (item.programStartsOn) {
    return formatDate(item.programStartsOn, { locale, style: 'medium' });
  }
  return '—';
};

const occurrenceForDate = (
  item: CalendarItem,
  date: string | null,
): CalendarOccurrenceStub | undefined => {
  if (!date) return undefined;
  return occurrenceStubForDate(item, date);
};

export const ChurchCalendarPage = () => {
  const { t } = useTranslation('programs');
  const navigate = useNavigate();
  const { locale, timezone } = usePreferences();
  const membership = useCurrentMembership();
  const canCreate = hasPermission(membership, PERMISSIONS.PROGRAMS_CREATE);
  const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');

  const [cursor, setCursor] = useState(() =>
    DateTime.now().setZone(timezone).startOf('month'),
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(today);
  const [createKind, setCreateKind] = useState<GatheringKind | null>(null);

  const monthStart = cursor.toFormat('yyyy-MM-dd');
  const monthEnd = cursor.endOf('month').toFormat('yyyy-MM-dd');

  const programs = useQuery({
    queryKey: ['programs', 'calendar', monthStart, monthEnd],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: '100',
        fromDate: monthStart,
        toDate: monthEnd,
      });
      return api.get<{ data: CalendarItem[] }>(`/programs/calendar?${params}`);
    },
    placeholderData: keepPreviousData,
  });

  const weekStart = useMemo(() => firstDayOfWeek(locale), [locale]);
  const weekdayNames = useMemo(() => {
    const names = Info.weekdays('short', { locale });
    return [...names.slice(weekStart - 1), ...names.slice(0, weekStart - 1)];
  }, [locale, weekStart]);

  const days = useMemo(() => buildGrid(cursor, weekStart), [cursor, weekStart]);

  const byDate = useMemo(
    () => indexProgramsByDate(programs.data?.data ?? []),
    [programs.data?.data],
  );

  const selected = selectedDate ? (byDate.get(selectedDate) ?? []) : [];
  const monthLabel = cursor.setLocale(locale).toFormat('LLLL yyyy');
  const loading = programs.isLoading;

  return (
    <div>
      <PageHeader
        title={t('calendar.title')}
        description={t('calendar.description')}
        headerActions={
          canCreate ? (
            <>
              <Button variant="outline" onClick={() => setCreateKind('service')}>
                {t('calendar.addService')}
              </Button>
              <Button onClick={() => setCreateKind('program')}>{t('calendar.addProgram')}</Button>
            </>
          ) : undefined
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Card>
          <CardContent className="space-y-4 pt-4">
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={t('calendar.previousMonth')}
                onClick={() => setCursor((current) => current.minus({ months: 1 }))}
              >
                <ChevronLeft className="size-4" aria-hidden />
              </Button>
              <h2 className="text-card-title font-semibold text-fg">{monthLabel}</h2>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const now = DateTime.now().setZone(timezone).startOf('month');
                    setCursor(now);
                    setSelectedDate(today);
                  }}
                >
                  {t('calendar.today')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-label={t('calendar.nextMonth')}
                  onClick={() => setCursor((current) => current.plus({ months: 1 }))}
                >
                  <ChevronRight className="size-4" aria-hidden />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1">
              {weekdayNames.map((name) => (
                <div
                  key={name}
                  className="px-1 py-1 text-center text-caption font-medium text-fg-secondary"
                >
                  {name}
                </div>
              ))}
              {days.map((date, index) => {
                if (!date) {
                  return <div key={`pad-${index}`} className="min-h-32 rounded-md bg-surface-sunken/40" />;
                }
                const items = byDate.get(date) ?? [];
                const isToday = date === today;
                const isSelected = date === selectedDate;
                return (
                  <button
                    key={date}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    className={cn(
                      'flex min-h-32 flex-col gap-1 rounded-md border p-1.5 text-left transition-colors',
                      isSelected
                        ? 'border-brand bg-brand/5'
                        : 'border-border bg-surface hover:border-brand/40',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex size-6 items-center justify-center rounded-full text-caption font-medium',
                        isToday ? 'bg-brand text-on-brand' : 'text-fg',
                      )}
                    >
                      {DateTime.fromISO(date).day}
                    </span>
                    <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden">
                      {items.slice(0, 3).map((item) => (
                        <span
                          key={item.programId}
                          className={cn(
                            'truncate rounded px-1 py-0.5 text-[0.65rem] leading-tight',
                            item.programKind === 'service'
                              ? 'bg-info-subtle text-info-fg'
                              : 'bg-success-subtle text-success-fg',
                          )}
                        >
                          {item.programName ?? t('calendar.untitled')}
                        </span>
                      ))}
                      {items.length > 3 ? (
                        <span className="text-[0.65rem] text-fg-tertiary">
                          {t('calendar.more', { count: items.length - 3 })}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="flex justify-center py-2">
                <Spinner className="size-5 text-brand" />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 pt-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">
                {selectedDate
                  ? DateTime.fromISO(selectedDate)
                      .setLocale(locale)
                      .toFormat('cccc, d LLLL yyyy')
                  : t('calendar.pickDay')}
              </h2>
              <Tip tipId="programs.calendar.day" />
            </div>

            {selected.length === 0 ? (
              <p className="text-body-sm text-fg-secondary">{t('calendar.dayEmpty')}</p>
            ) : (
              <ul className="space-y-2">
                {selected.map((item) => {
                  const dayOccurrence = occurrenceForDate(item, selectedDate);
                  const startsAt = dayOccurrence?.startsAt ?? item.programStartsAt;
                  const endsAt = dayOccurrence?.endsAt ?? item.programEndsAt;
                  return (
                  <li key={item.programId}>
                    <button
                      type="button"
                      onClick={() =>
                        void navigate(detailPathFor(item.programKind, item.programId))
                      }
                      className="block w-full rounded-md border border-border p-3 text-left hover:border-brand/40"
                    >
                      <span className="font-medium text-fg">
                        {item.programName ?? t('calendar.untitled')}
                      </span>
                      <p className="mt-1 text-body-sm text-fg-secondary">
                        {programDateLabel(item, locale, selectedDate)}
                      </p>
                      {startsAt && endsAt ? (
                        <p className="text-body-sm text-fg-secondary">
                          {t('calendar.timeVenue', {
                            time: t('timeRange', {
                              start: formatTime(startsAt),
                              end: formatTime(endsAt),
                            }),
                            venue: item.venueName || '—',
                          })}
                        </p>
                      ) : item.venueName ? (
                        <p className="text-body-sm text-fg-secondary">{item.venueName}</p>
                      ) : null}
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {createKind ? (
        <GatheringFormDialog
          kind={createKind}
          open
          defaultDate={selectedDate}
          onOpenChange={(next) => {
            if (!next) setCreateKind(null);
          }}
        />
      ) : null}
    </div>
  );
};
